"use client"

import { useEffect, useState, useRef } from "react"
import { Quote, ChevronLeft, ChevronRight, MessageSquare } from "lucide-react"
import axios from "axios"
import PaintSplatter from "./paint-splatter"

export default function Testimonials({ profile }) {
  const [activeSlide, setActiveSlide] = useState(0)
  const intervalRef = useRef(null)
  const [testimonials, setTestimonials] = useState([])

  useEffect(() => {
    // Fetch testimonials for this professional
    const fetchTestimonials = async () => {
      if (!profile || !profile.professional_id) return
      try {
        const res = await axios.get(
          `http://localhost:5000/professional-testimonials/${profile.professional_id}`,
        )
        setTestimonials(res.data)
      } catch (err) {
        setTestimonials([])
      }
    }
    fetchTestimonials()
  }, [profile])

  useEffect(() => {
    startAutoSlide()
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [testimonials.length])

  const startAutoSlide = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    if (testimonials.length > 1) {
      intervalRef.current = setInterval(() => {
        setActiveSlide((prev) => (prev + 1) % testimonials.length)
      }, 5000)
    }
  }

  const goToSlide = (index) => {
    setActiveSlide(index)
    startAutoSlide()
  }

  const containerStyle = {
    backgroundColor: "#f3f4f6",
    padding: "1.25rem 0",
    margin: "1.25rem 0",
    position: "relative",
  }

  const innerContainerStyle = {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "1.25rem 1rem",
    position: "relative",
    zIndex: 10,
  }

  const headingStyle = {
    fontSize: "2.25rem",
    textAlign: "center",
    marginBottom: "1.25rem",
    fontWeight: "bold",
    animation: "fadeIn 1s ease-out forwards",
  }

  const contentContainerStyle = {
    display: "flex",
    justifyContent: "center",
  }

  const sideColumnStyle = {
    display: "none",
    width: "25%",
    position: "relative",
  }

  const centerColumnStyle = {
    width: "100%",
    animation: "fadeIn 1s ease-out forwards",
    animationDelay: "0.5s",
  }

  const testimonialStyle = (isActive) => ({
    textAlign: "center",
    transition: "opacity 0.5s",
    opacity: isActive ? 1 : 0,
    position: isActive ? "relative" : "absolute",
    inset: isActive ? "auto" : 0,
  })

  const imageContainerStyle = {
    position: "relative",
    marginBottom: "1.25rem",
  }

  const imageSplatterStyle = {
    position: "absolute",
    top: "-0.75rem",
    right: "-0.75rem",
    width: "3rem",
    height: "3rem",
    backgroundColor: "#FFBA07",
    opacity: 0.3,
    borderRadius: "9999px",
    filter: "blur(0.5rem)",
  }

  const imageStyle = {
    width: "6rem",
    height: "6rem",
    borderRadius: "9999px",
    border: "1px solid #d1d5db",
    padding: "0.5rem",
    margin: "0 auto",
    position: "relative",
    zIndex: 10,
  }

  const quoteIconContainerStyle = {
    bottom: "-0.75rem",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "white",
    borderRadius: "9999px",
    padding: "0.5rem",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    zIndex: 20,
    position: "absolute",
    marginBottom: "1.25rem",
  }

  const quoteIconStyle = {
    color: "#7749F8",
    width: "1.25rem",
    height: "1.25rem",
  }

  const testimonialTextStyle = {
    fontSize: "1.125rem",
    fontStyle: "italic",
  }

  const dividerStyle = {
    width: "25%",
    margin: "0.75rem auto",
  }

  const nameStyle = {
    fontWeight: "500",
  }

  const professionStyle = {
    color: "#6b7280",
  }

  const dotsContainerStyle = {
    display: "flex",
    justifyContent: "center",
    marginTop: "1.25rem",
  }

  const dotStyle = (isActive, index) => ({
    width: "0.75rem",
    height: "0.75rem",
    borderRadius: "9999px",
    margin: "0 0.25rem",
    backgroundColor: isActive ? (index === 0 ? "#7749F8" : index === 1 ? "#FFBA07" : "#f97316") : "#d1d5db",
    border: "none",
    cursor: "pointer",
  })

  const sideImageStyle = (index) => ({
    position: "absolute",
    borderRadius: "0.5rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    width: "75%",
    transition: "all 1s",
    ...(index === 0 ? { top: 0, left: 0 } : index === 1 ? { top: "33%", left: "25%" } : { top: "66%", left: 0 }),
    animation: "fadeIn 1s ease-out forwards",
    animationDelay: `${index * 0.2}s`,
    zIndex: 10,
  })

  const sideImageSplatterStyle = {
    position: "absolute",
    top: "-0.75rem",
    left: "-0.75rem",
    width: "3rem",
    height: "3rem",
    backgroundColor: "#FFBA07",
    opacity: 0.3,
    borderRadius: "9999px",
    filter: "blur(0.5rem)",
  }

  // Media query styles to be applied with JavaScript
  useEffect(() => {
    const handleResize = () => {
      const isLargeScreen = window.innerWidth >= 1024

      // Update styles based on screen size
      const leftColumn = document.getElementById("testimonial-left-column")
      const rightColumn = document.getElementById("testimonial-right-column")
      const centerColumn = document.getElementById("testimonial-center-column")

      if (leftColumn && rightColumn && centerColumn) {
        if (isLargeScreen) {
          leftColumn.style.display = "block"
          rightColumn.style.display = "block"
          centerColumn.style.width = "50%"
        } else {
          leftColumn.style.display = "none"
          rightColumn.style.display = "none"
          centerColumn.style.width = "100%"
        }
      }
    }

    // Initial call and event listener
    handleResize()
    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  return (
    <div style={containerStyle} id="testimonial">
      {/* Paint splatters */}
      <PaintSplatter position="top-left" size="lg" opacity={0.1} rotation={-15} />
      <PaintSplatter position="bottom-right" size="lg" opacity={0.1} rotation={25} />
      <PaintSplatter position="center-left" size="md" opacity={0.15} rotation={-10} />
      <PaintSplatter position="center-right" size="md" opacity={0.15} rotation={10} />

      <div style={innerContainerStyle}>
        <div className="text-center mb-12 space-y-2">
        <h1 style={headingStyle}>Testimonial</h1>
        <p className="text-slate-600 max-w-2xl mx-auto">
            What businesses said about working with me
          </p>
        </div>
        <div style={contentContainerStyle}>
          <div id="testimonial-center-column" style={centerColumnStyle}>
            <div style={{ position: "relative" }}>
              {testimonials.length === 0 ? (
                <div style={{ textAlign: "center", color: "#6b7280", minHeight: "120px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  No testimonials yet.
                </div>
              ) : (
                testimonials.map((testimonial, index) => (
                  <div key={index} style={testimonialStyle(activeSlide === index)}>
                    <div className="relative mx-auto w-16 h-16 mb-6">
                          <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-rose-400 rounded-full blur-sm -z-10 scale-95 opacity-30" />
                          <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white flex items-center justify-center bg-gray-100">
                            <span className="text-2xl font-bold text-teal-500">
                              {testimonial.client_name?.charAt(0) || "C"}
                            </span>
                          </div>
                          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white rounded-full p-1 shadow-sm">
                            <Quote className="h-4 w-4 text-teal-500" />
                          </div>
                        </div>
                    <p style={testimonialTextStyle}>{testimonial.comment || "No comment provided."}</p>
                    <hr style={dividerStyle} />
                    <h5 style={nameStyle}>{testimonial.client_name || "Client"}</h5>
                    <span style={professionStyle}>
                      {testimonial.rating ? (
                        <span>
                          {Array.from({ length: testimonial.rating }, (_, i) => (
                            <span key={i} style={{ color: "#facc15" }}>&#9733;</span>
                          ))}
                          {Array.from({ length: 5 - testimonial.rating }, (_, i) => (
                            <span key={i} style={{ color: "#d1d5db" }}>&#9733;</span>
                          ))}
                        </span>
                      ) : (
                        "No rating"
                      )}
                    </span>
                  </div>
                ))
              )}
            </div>
            <div style={dotsContainerStyle}>
              {testimonials.map((_, index) => (
                <button key={index} style={dotStyle(activeSlide === index, index)} onClick={() => goToSlide(index)} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

